import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Loader2, RotateCcw, Trophy, Sparkles, Gift, Radio } from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SorteoBroadcast } from "@/components/sorteo/SorteoBroadcast";
import { AdminLiveMicSection } from "@/components/admin/AdminLiveMicSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_PRESETS = [
  "Lectura Online Normal",
  "Lectura Online Completa",
  "Lectura de Runas",
  "Magia Suprema",
  "Poder Máximo",
];

/**
 * Modern animated roulette to randomly pick a winning user during TikTok lives.
 * - Live-syncs eligible users via SSE
 * - Allows custom or preset service prize
 * - Variable spin force (slider)
 * - Modern conic-gradient wheel with names
 * - Confetti + winner card on stop
 */
export const AdminSorteoTab = () => {
  const { authHeader } = useAuth();
  const [users, setUsers] = useState([]);
  const [service, setService] = useState(SERVICE_PRESETS[0]);
  const [customService, setCustomService] = useState("");
  const [force, setForce] = useState(70);  // 0-100 spin force
  const [spinning, setSpinning] = useState(false);
  const [angle, setAngle] = useState(0);
  const [winner, setWinner] = useState(null);
  const [history, setHistory] = useState([]);
  const wheelRef = useRef(null);

  const load = async () => {
    try {
      const [ur, hr] = await Promise.all([
        axios.get(`${API}/admin/sorteo/users`, { headers: authHeader() }),
        axios.get(`${API}/admin/sorteo/history`, { headers: authHeader() }),
      ]);
      setUsers(ur.data?.users || []);
      setHistory(hr.data || []);
    } catch { toast.error("Error cargando sorteo"); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useRealtimeEvents({
    admin_refresh: (d) => { if (d?.scope === "users" || d?.scope === "sorteo") load(); },
    presence: () => { /* keep list fresh */ },
  });

  const finalService = (customService || service || "").trim();
  const N = users.length;
  const segments = users; // 1-to-1 mapping; each user = one wedge

  const spin = () => {
    if (spinning) return;
    if (N === 0) return toast.error("No hay usuarios elegibles");
    if (!finalService) return toast.error("Indica el servicio a sortear");
    setWinner(null);
    // Pick winner uniformly at random
    const winIdx = Math.floor(Math.random() * N);
    const segSize = 360 / N;
    // Force 30 → ~3 spins, 100 → ~10 spins
    const turns = 3 + (force / 100) * 8;
    // Land so the chosen segment is at the top (angle 270deg in standard wheel)
    // The pointer is at 0° (top). We want angle such that (-angle + winIdx*segSize + segSize/2) ≡ 0 (mod 360)
    // => angle ≡ winIdx*segSize + segSize/2 (mod 360)
    const finalLocal = winIdx * segSize + segSize / 2;
    const target = 360 * turns + (360 - finalLocal);
    setSpinning(true);
    setAngle(target);
    const duration = 4000 + (force * 30); // 4–7s
    setTimeout(async () => {
      const w = segments[winIdx];
      setWinner(w);
      setSpinning(false);
      try {
        confetti({ particleCount: 200, spread: 90, origin: { y: 0.5 } });
      } catch { /* noop */ }
      // Record
      try {
        await axios.post(`${API}/admin/sorteo/record`, {
          service_name: finalService,
          winner_id: w.id,
          winner_username: w.username,
        }, { headers: authHeader() });
        toast.success(`Ganador: ${w.name || w.username}`);
        load();
      } catch { /* ignore */ }
    }, duration + 200);
  };

  const reset = () => {
    setWinner(null);
    setAngle(0);
    setSpinning(false);
  };

  // Build conic-gradient string with alternating colors
  const buildGradient = () => {
    if (N === 0) return "conic-gradient(#222, #333)";
    const palette = ["#d4af37", "#8b5cf6", "#ec4899", "#10b981", "#f59e0b", "#3b82f6", "#ef4444"];
    const segSize = 360 / N;
    const stops = users.map((_, i) => {
      const color = palette[i % palette.length];
      return `${color} ${i * segSize}deg ${(i + 1) * segSize}deg`;
    }).join(", ");
    return `conic-gradient(${stops})`;
  };

  return (
    <div className="space-y-5" data-testid="admin-sorteo-tab">
      <AdminLiveMicSection />
      <div className="glass-dark rounded-2xl p-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Wheel column */}
        <div className="lg:col-span-2 flex flex-col items-center">
          <div className="relative" style={{ width: 360, height: 360, maxWidth: "100%" }}>
            {/* Pointer */}
            <div className="absolute left-1/2 -translate-x-1/2 -top-3 z-10">
              <div className="w-0 h-0 border-l-[16px] border-l-transparent border-r-[16px] border-r-transparent border-t-[26px] border-t-[#d4af37] drop-shadow-2xl" />
            </div>
            <div
              ref={wheelRef}
              data-testid="sorteo-wheel"
              className="w-full h-full rounded-full shadow-2xl border-4 border-[#d4af37]/40 relative overflow-hidden"
              style={{
                background: buildGradient(),
                transform: `rotate(${angle}deg)`,
                transition: spinning ? `transform ${4 + (force * 0.03)}s cubic-bezier(0.17, 0.67, 0.12, 0.99)` : "transform 0.3s ease-out",
              }}
            >
              {/* User labels */}
              {users.map((u, i) => {
                const segSize = 360 / N;
                const rot = i * segSize + segSize / 2;
                return (
                  <div
                    key={u.id}
                    className="absolute top-1/2 left-1/2 origin-left text-white font-bold pointer-events-none"
                    style={{
                      transform: `translateY(-50%) rotate(${rot}deg) translateX(20%)`,
                      width: "50%",
                      fontSize: N > 30 ? 9 : N > 15 ? 11 : 13,
                      textShadow: "0 1px 3px rgba(0,0,0,0.7)",
                    }}
                  >
                    <span className="block truncate pr-4">{u.username}</span>
                  </div>
                );
              })}
            </div>
            {/* Center hub */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-gradient-to-br from-[#d4af37] to-amber-700 border-4 border-black/40 flex items-center justify-center shadow-inner">
              <Sparkles className="w-7 h-7 text-black" />
            </div>
          </div>

          <div className="flex items-center gap-2 mt-5 flex-wrap justify-center">
            <button
              data-testid="sorteo-spin"
              onClick={spin}
              disabled={spinning || N === 0}
              className="btn-gold px-6 py-3 rounded-full text-sm uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
            >
              {spinning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {spinning ? "Girando..." : "GIRAR RULETA"}
            </button>
            <button
              data-testid="sorteo-reset"
              onClick={reset}
              disabled={spinning}
              className="btn-outline-gold px-4 py-3 rounded-full text-sm uppercase tracking-widest flex items-center gap-2"
            ><RotateCcw className="w-3.5 h-3.5" /> Reiniciar</button>
          </div>
        </div>

        {/* Controls column */}
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-300">Servicio a sortear</Label>
            <div className="space-y-1 mt-1">
              {SERVICE_PRESETS.map((s) => (
                <button
                  key={s}
                  data-testid={`sorteo-preset-${s.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => { setService(s); setCustomService(""); }}
                  className={`block w-full text-left text-xs px-3 py-1.5 rounded border transition ${service === s && !customService ? "border-[#d4af37] bg-[#d4af37]/10 text-[#d4af37]" : "border-white/10 text-neutral-300 hover:text-white"}`}
                >{s}</button>
              ))}
            </div>
            <Input
              data-testid="sorteo-custom-service"
              placeholder="O escribe servicio personalizado..."
              value={customService}
              onChange={(e) => setCustomService(e.target.value)}
              className="mt-2 bg-black/40 border-white/10 text-white text-xs"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-300 flex items-center justify-between">
              <span>Fuerza de giro</span>
              <span className="text-[#d4af37] font-mono">{force}%</span>
            </Label>
            <input
              type="range" min="20" max="100" step="5"
              value={force}
              onChange={(e) => setForce(Number(e.target.value))}
              data-testid="sorteo-force"
              className="w-full mt-2 accent-[#d4af37]"
            />
            <div className="text-[10px] text-neutral-500 flex justify-between"><span>Suave</span><span>Frenético</span></div>
          </div>
          <div className="bg-black/30 border border-white/5 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-widest text-neutral-400">Usuarios elegibles</div>
            <div className="text-2xl font-bold text-white">{N}</div>
            <div className="text-[10px] text-neutral-500">Probabilidad: {N > 0 ? (100 / N).toFixed(2) : 0}%</div>
          </div>
        </div>
      </div>

      {/* Winner card */}
      {winner && (
        <div data-testid="sorteo-winner-card" className="glass-dark rounded-2xl p-6 border-2 border-[#d4af37] shadow-2xl shadow-amber-500/40 animate-pulse-slow">
          <div className="text-center">
            <Trophy className="w-12 h-12 text-[#d4af37] mx-auto mb-2 animate-bounce" />
            <div className="text-xs uppercase tracking-widest text-[#d4af37]">¡Tenemos ganador!</div>
            <div className="font-serif text-3xl text-white mt-2">🎉 {winner.name || winner.username} 🎉</div>
            <div className="text-sm text-neutral-300 mt-1">@{winner.username} · <span className="font-mono">{winner.visible_id}</span></div>
            <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/40 text-[#d4af37]">
              <Gift className="w-4 h-4" /> {finalService}
            </div>
          </div>
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="glass-dark rounded-2xl p-4">
          <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2"><Trophy className="w-4 h-4 text-[#d4af37]" /> Historial de Sorteos</h3>
          <div className="space-y-1">
            {history.slice(0, 20).map((h) => (
              <div key={h.id} className="flex items-center justify-between text-xs bg-black/30 rounded p-2 border border-white/5">
                <div>
                  <span className="text-white font-semibold">{h.winner_username}</span>
                  <span className="text-neutral-400 ml-2">· {h.service_name}</span>
                </div>
                <span className="text-[10px] text-neutral-500">{new Date(h.at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse-slow { 0%, 100% { box-shadow: 0 0 30px rgba(212,175,55,0.4); } 50% { box-shadow: 0 0 60px rgba(212,175,55,0.8); } }
        .animate-pulse-slow { animation: pulse-slow 2.5s ease-in-out infinite; }
      `}</style>

      <LiveSorteoSection />
    </div>
  );
};


// =====================================================================
// LIVE SORTEO SECTION — Real-time interactive sorteo with crowd voting
// =====================================================================
const LiveSorteoSection = () => {
  const { authHeader, token } = useAuth();
  const [list, setList] = useState([]);
  const [creating, setCreating] = useState(false);
  const [broadcastingFor, setBroadcastingFor] = useState(null);
  const [liveState, setLiveState] = useState(null);
  const [savingBtn, setSavingBtn] = useState(false);
  const [editingUrl, setEditingUrl] = useState(false);
  const [tiktokUrl, setTiktokUrl] = useState("");
  const [form, setForm] = useState({
    title: "",
    prize: "",
    candidates: "",
    voting_seconds: 60,
    include_all_users: true,
  });

  const loadLiveState = async () => {
    try {
      const r = await axios.get(`${API}/sorteo/live-state`);
      setLiveState(r.data);
      if (!editingUrl) setTiktokUrl(r.data?.tiktok_live_url || "");
    } catch { /* ignore */ }
  };

  const saveTiktokUrl = async () => {
    try {
      await axios.post(
        `${API}/admin/sorteo/live-button`,
        { enabled: !!liveState?.button_enabled, tiktok_live_url: tiktokUrl.trim() },
        { headers: authHeader() }
      );
      toast.success("URL del live de TikTok actualizada");
      setEditingUrl(false);
      await loadLiveState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar la URL");
    }
  };

  const toggleLiveBtn = async (enabled) => {
    setSavingBtn(true);
    try {
      await axios.post(`${API}/admin/sorteo/live-button`, { enabled }, { headers: authHeader() });
      toast.success(enabled ? "🟢 Botón En Vivo VISIBLE para usuarios" : "🔴 Botón En Vivo OCULTO");
      await loadLiveState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSavingBtn(false); }
  };

  const resetWinnersToday = async () => {
    if (!window.confirm("¿Reiniciar la lista de ganadores del día? Los sorteos quedan en el historial, pero ya no aparecerán como ganadores de HOY.")) return;
    try {
      await axios.post(`${API}/admin/sorteo/winners/reset-today`, {}, { headers: authHeader() });
      toast.success("Ganadores del día reiniciados");
      await loadLiveState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/admin/sorteo/list`, { headers: authHeader() });
      setList(r.data || []);
    } catch { /* noop */ }
  };

  useEffect(() => { refresh(); loadLiveState(); }, []); // eslint-disable-line

  useRealtimeEvents({
    sorteo_update: () => { refresh(); loadLiveState(); },
    admin_refresh: (d) => { if (d?.scope === "sorteo") { refresh(); loadLiveState(); } },
  });

  const create = async () => {
    const manualNames = form.candidates.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
    if (!form.title.trim() || !form.prize.trim()) {
      return toast.error("Título y premio son obligatorios");
    }
    if (!form.include_all_users && manualNames.length < 2) {
      return toast.error("Si desactivas 'incluir a todos los usuarios', añade mínimo 2 candidatos");
    }
    setCreating(true);
    try {
      const body = {
        title: form.title.trim(),
        prize: form.prize.trim(),
        voting_seconds: Number(form.voting_seconds) || 60,
        include_all_users: !!form.include_all_users,
      };
      if (!form.include_all_users) {
        body.candidates = manualNames.map((n) => ({ name: n }));
      }
      await axios.post(`${API}/admin/sorteo/create`, body, { headers: authHeader() });
      toast.success(form.include_all_users
        ? "Sorteo creado e iniciado con TODOS los usuarios"
        : "Sorteo creado");
      setForm({ title: "", prize: "", candidates: "", voting_seconds: 60, include_all_users: true });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error creando sorteo");
    } finally {
      setCreating(false);
    }
  };

  const start = async (id) => {
    try {
      await axios.post(`${API}/admin/sorteo/${id}/start`, {}, { headers: authHeader() });
      toast.success("Votación iniciada");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const spin = async (id) => {
    try {
      const r = await axios.post(`${API}/admin/sorteo/${id}/spin`, {}, { headers: authHeader() });
      toast.success(`Ganador: ${r.data?.winner?.name}`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const hide = async (id) => {
    try {
      await axios.post(`${API}/admin/sorteo/${id}/hide`, {}, { headers: authHeader() });
      refresh();
    } catch { /* noop */ }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este sorteo y su ganador del historial?")) return;
    try {
      await axios.delete(`${API}/admin/sorteo/${id}`, { headers: authHeader() });
      toast.success("Sorteo eliminado");
      refresh();
    } catch { toast.error("Error"); }
  };

  return (
    <div className="mt-8 border-t border-white/10 pt-6" data-testid="live-sorteo-admin">
      <h3 className="font-serif text-xl text-white mb-1 flex items-center gap-2">
        <Gift className="w-5 h-5 text-[#d4af37]" /> Sorteo Interactivo (Vivo)
      </h3>
      <p className="text-xs text-neutral-400 mb-4">
        Los usuarios ven un botón flotante con la ruleta y votan el poder del giro (Suave / Normal / Fuerte). El poder más votado define la velocidad y dramatismo del giro.
      </p>

      {/* Live broadcast button + day-winners control */}
      <div className="mb-5 rounded-xl border border-rose-500/30 bg-rose-500/5 p-4" data-testid="live-btn-control">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-rose-300 font-bold mb-1 flex items-center gap-1.5">
              <Radio className="w-3.5 h-3.5" /> Botón &ldquo;En Vivo&rdquo; (TikTok)
            </div>
            <p className="text-[11px] text-neutral-400 leading-snug">
              Cuando esté activo, todos los visitantes verán un botón flotante que abre tu live de TikTok + la ruleta lado a lado.
            </p>
          </div>
          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              data-testid="toggle-live-btn-on"
              onClick={() => toggleLiveBtn(true)}
              disabled={savingBtn || !!liveState?.button_enabled}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest font-bold border transition ${
                liveState?.button_enabled
                  ? "bg-emerald-500 text-black border-emerald-300"
                  : "border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/20"
              }`}
            >
              {liveState?.button_enabled ? "✓ Visible" : "Mostrar"}
            </button>
            <button
              type="button"
              data-testid="toggle-live-btn-off"
              onClick={() => toggleLiveBtn(false)}
              disabled={savingBtn || !liveState?.button_enabled}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest font-bold border transition ${
                !liveState?.button_enabled
                  ? "bg-neutral-600 text-white border-neutral-500"
                  : "border-red-400/40 text-red-200 hover:bg-red-500/20"
              }`}
            >
              {!liveState?.button_enabled ? "Oculto" : "Ocultar"}
            </button>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="text-[11px] text-neutral-400">
              Ganadores hoy: <span data-testid="admin-winners-today-count" className="text-[#d4af37] font-bold text-base">{liveState?.winners_today_count ?? 0}</span>
            </div>
            <button
              type="button"
              data-testid="reset-winners-today"
              onClick={resetWinnersToday}
              disabled={(liveState?.winners_today_count ?? 0) === 0}
              className="text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border border-amber-400/30 text-amber-200 hover:bg-amber-500/15 disabled:opacity-40"
            >
              Reiniciar ganadores del día
            </button>
          </div>
        </div>

        {/* TikTok URL editor */}
        <div className="mt-4 pt-4 border-t border-rose-500/15">
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-[10px] uppercase tracking-widest text-rose-300 font-bold whitespace-nowrap">
              URL del live de TikTok
            </label>
            <input
              type="url"
              data-testid="tiktok-live-url-input"
              value={editingUrl ? tiktokUrl : (liveState?.tiktok_live_url || "")}
              readOnly={!editingUrl}
              onChange={(e) => setTiktokUrl(e.target.value)}
              onFocus={() => { if (!editingUrl) { setTiktokUrl(liveState?.tiktok_live_url || ""); setEditingUrl(true); } }}
              placeholder="https://www.tiktok.com/@usuario/live"
              className={`flex-1 min-w-[280px] px-3 py-1.5 rounded-md border bg-black/40 font-mono text-[11px] outline-none transition ${
                editingUrl ? "border-amber-400/60 text-amber-200 focus:border-amber-400" : "border-white/10 text-neutral-300"
              }`}
            />
            {editingUrl ? (
              <>
                <button
                  type="button"
                  data-testid="tiktok-url-save"
                  onClick={saveTiktokUrl}
                  className="text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-md bg-emerald-500 text-black font-bold hover:bg-emerald-400"
                >
                  Guardar
                </button>
                <button
                  type="button"
                  data-testid="tiktok-url-cancel"
                  onClick={() => { setEditingUrl(false); setTiktokUrl(liveState?.tiktok_live_url || ""); }}
                  className="text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-md border border-white/15 text-neutral-300 hover:text-white"
                >
                  Cancelar
                </button>
              </>
            ) : (
              <button
                type="button"
                data-testid="tiktok-url-edit"
                onClick={() => { setTiktokUrl(liveState?.tiktok_live_url || ""); setEditingUrl(true); }}
                className="text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-md border border-amber-400/30 text-amber-200 hover:bg-amber-500/15"
              >
                Editar
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <Input
          data-testid="live-sorteo-title"
          placeholder="Título del sorteo"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="bg-black/40 border-white/10 text-white"
        />
        <Input
          data-testid="live-sorteo-prize"
          placeholder="Premio (ej: Lectura Básica gratis)"
          value={form.prize}
          onChange={(e) => setForm({ ...form, prize: e.target.value })}
          className="bg-black/40 border-white/10 text-white"
        />
        <label className="flex items-center gap-2 md:col-span-2 px-3 py-2 rounded bg-black/30 border border-[#d4af37]/30 cursor-pointer">
          <input
            type="checkbox"
            data-testid="live-sorteo-all-users"
            checked={form.include_all_users}
            onChange={(e) => setForm({ ...form, include_all_users: e.target.checked })}
            className="accent-[#d4af37]"
          />
          <span className="text-xs text-neutral-200">
            Incluir <b className="text-[#d4af37]">todos los usuarios registrados</b> automáticamente y dar acceso al sorteo en vivo al crear.
          </span>
        </label>
        {!form.include_all_users && (
          <Input
            data-testid="live-sorteo-candidates"
            placeholder="Candidatos manuales separados por coma"
            value={form.candidates}
            onChange={(e) => setForm({ ...form, candidates: e.target.value })}
            className="bg-black/40 border-white/10 text-white md:col-span-2"
          />
        )}
        <div className="flex items-center gap-2">
          <Label className="text-xs text-neutral-400">Segundos de votación:</Label>
          <Input
            type="number"
            min="15"
            max="300"
            value={form.voting_seconds}
            onChange={(e) => setForm({ ...form, voting_seconds: Number(e.target.value) })}
            className="bg-black/40 border-white/10 text-white w-24"
          />
        </div>
        <button
          data-testid="live-sorteo-create"
          onClick={create}
          disabled={creating}
          className="btn-gold py-2.5 rounded-lg uppercase text-xs tracking-widest disabled:opacity-60"
        >
          {creating ? "Creando..." : (form.include_all_users ? "Crear e Iniciar Ahora" : "Crear Sorteo")}
        </button>
      </div>

      <div className="space-y-2">
        {list.length === 0 && (
          <div className="text-xs text-neutral-500 italic p-3 bg-black/30 rounded">Sin sorteos aún. Crea uno arriba.</div>
        )}
        {list.map((s) => (
          <div key={s.id} className="bg-black/30 rounded-lg p-3 border border-white/10 flex flex-wrap items-center justify-between gap-2" data-testid={`live-sorteo-${s.id}`}>
            <div className="flex-1 min-w-[200px]">
              <div className="text-sm text-white font-semibold flex items-center gap-2">
                {s.title}
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${
                  s.status === "voting" ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" :
                  s.status === "spinning" ? "bg-amber-500/20 text-amber-300 border border-amber-500/40" :
                  s.status === "completed" ? "bg-purple-500/20 text-purple-300 border border-purple-500/40" :
                  "bg-neutral-500/20 text-neutral-400 border border-neutral-500/40"
                }`}>
                  {s.status}{s.hidden ? " · oculto" : ""}
                </span>
              </div>
              <div className="text-[11px] text-neutral-400">
                {s.prize} · {(s.candidates || []).length} candidatos · {s.voters_count || 0} votos
                {s.winner && <span className="ml-2 text-[#d4af37]">🏆 {s.winner.name}</span>}
              </div>
            </div>
            <div className="flex gap-1">
              {(s.status === "draft" || s.status === "completed" || s.status === "hidden") && (
                <button onClick={() => start(s.id)} className="text-xs px-2 py-1 rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" data-testid={`live-sorteo-start-${s.id}`}>Iniciar</button>
              )}
              {(s.status === "voting" || s.status === "spinning") && s.daily_room_url && (
                <button
                  onClick={() => setBroadcastingFor((cur) => (cur === s.id ? null : s.id))}
                  className={`text-xs px-2 py-1 rounded border flex items-center gap-1 ${
                    broadcastingFor === s.id
                      ? "border-red-500/60 text-red-300 bg-red-500/15"
                      : "border-sky-500/40 text-sky-300 hover:bg-sky-500/10"
                  }`}
                  data-testid={`live-sorteo-broadcast-${s.id}`}
                  title={broadcastingFor === s.id ? "Detener transmisión" : "Iniciar transmisión en vivo"}
                >
                  <Radio className="w-3 h-3" /> {broadcastingFor === s.id ? "En vivo" : "Transmitir"}
                </button>
              )}
              {s.status === "voting" && (
                <button onClick={() => spin(s.id)} className="text-xs px-2 py-1 rounded border border-amber-500/40 text-amber-300 hover:bg-amber-500/10" data-testid={`live-sorteo-spin-${s.id}`}>Girar ahora</button>
              )}
              <button onClick={() => hide(s.id)} className="text-xs px-2 py-1 rounded border border-white/20 text-neutral-300 hover:bg-white/5" data-testid={`live-sorteo-hide-${s.id}`}>{s.hidden ? "Mostrar" : "Ocultar"}</button>
              <button onClick={() => del(s.id)} className="text-xs px-2 py-1 rounded border border-red-500/30 text-red-300 hover:bg-red-500/10" data-testid={`live-sorteo-delete-${s.id}`}>Eliminar</button>
            </div>
            {broadcastingFor === s.id && (
              <div className="w-full mt-3" data-testid={`broadcast-frame-${s.id}`}>
                <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-2">
                  📡 Tu cámara y micrófono se están emitiendo a todos los usuarios. Cierra esta tarjeta para terminar.
                </div>
                <SorteoBroadcast sorteoId={s.id} token={token} isAdmin={true} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
